import { SectionStatus, SectionStatusType } from '../data-qa-constants';
import { formatModelNameList } from '../data-qa-utils';
import { ReviewNotice, ReviewNoticeKind } from './ReviewNoticeContext';

interface InheritedReview {
  sourceModelLabel: string;
  status: SectionStatusType;
}

interface DataInvariance {
  checking: boolean;
  matchingModelLabels: string[];
}

// The active step's cross-model notice, if any. A carried-over review wins (it changes
// what the reviewer has to do); otherwise other models with identical data, once the
// comparison has settled. `hasReview` is false for steps with no approve/flag
// (Campaigns), where there's no decision to carry over -- just a table not to re-check.
export const buildReviewNotice = (
  inherited: InheritedReview | undefined,
  invariance: DataInvariance | undefined,
  hasReview: boolean
): ReviewNotice | undefined => {
  const identicalModels = invariance?.matchingModelLabels ?? [];

  if (inherited) {
    const decision = inherited.status === SectionStatus.APPROVED ? 'Approved' : 'Flagged';
    const sameAs = formatModelNameList(
      identicalModels.length ? identicalModels : [inherited.sourceModelLabel]
    );
    return {
      kind: ReviewNoticeKind.INHERITED,
      label: `${decision} via ${inherited.sourceModelLabel}${identicalModels.length ? ' · same data' : ''}`,
      detail: `This data is identical to ${sameAs}, so ${inherited.sourceModelLabel}'s review carried over as ${inherited.status}. You can still override it.`
    };
  }

  if (invariance?.checking) {
    return {
      kind: ReviewNoticeKind.CHECKING,
      label: 'Checking other models…',
      detail: 'Checking whether other models share this exact data.'
    };
  }

  if (identicalModels.length === 0) return undefined;
  const names = formatModelNameList(identicalModels);
  return {
    kind: ReviewNoticeKind.IDENTICAL,
    label: `Same data as ${names}`,
    detail: hasReview
      ? `This data is identical to ${names}, so you only need to review it once: your decision here carries over when you open ${identicalModels.length === 1 ? 'it' : 'them'}. Every other model or stage still needs its own review.`
      : `This data is identical to ${names}, so you don't need to check it again there.`,
    approveHint: hasReview ? `Carries over to ${names} (identical data)` : undefined
  };
};
